// Faithful JS port of Socher Hayam (CODE.PAS) game logic for mobile web.
// Hebrew strings are written in LOGICAL order here and converted to the
// original visual order by vis() at render time.
import { vis, PALETTE } from "./screen";
import { sleep } from "./io";
import { Rng } from "./rng";
import { saveGame, loadGame, deleteSave, getLocalScores, addLocalScore, loadSettings } from "./save";
import { recordScore } from "./session";

export const COPPER = 1;
export const OLIVES = 2;
export const WHEAT = 3;
export const TURKEY = 1;
export const ISRAEL = 2;
export const EGYPT = 3;
const MAX_INT = 32767;

const COUNTRY = { 1: "תורכיה", 2: "ישראל", 3: "מצרים" };
const WARE = { 1: "נחושת", 2: "זיתים", 3: "חיטה" };
const COUNTRY_NAME = { 1: "תורכיה", 2: "ישראל", 3: "מצרים" };
const BASE = { 1: 3000, 2: 500, 3: 50 };
const DAYS = { 1: "ראשון", 2: "שני", 3: "שלישי", 4: "רביעי", 5: "חמישי", 6: "שישי", 7: "שביעי" };

const COORD = {
  1: [191, 23], 2: [198, 86], 3: [123, 113], 4: [198, 50],
  5: [161, 96], 6: [123, 40], 7: [123, 70], 8: [196, 45],
};
const WAYPOINTS = { 1: { 2: 4, 3: 6 }, 2: { 1: 4, 3: 5 }, 3: { 1: 6, 2: 5 }, 5: { 1: 8 }, 6: { 2: 7 } };

const API = process.env.REACT_APP_BACKEND_URL;

export class GameExit extends Error {
  constructor() {
    super("quit");
    this.gameExit = true;
  }
}

function commas(n) {
  return Math.trunc(n).toLocaleString("en-US");
}

function wn(n, width) {
  return String(Math.trunc(n)).padStart(width);
}

async function fetchScores(mode = "standard") {
  try {
    const res = await fetch(`${API}/api/highscores?limit=20&mode=${mode}`);
    if (!res.ok) throw new Error("bad status");
    const data = await res.json();
    return { scores: data.scores, online: true };
  } catch (e) {
    // local fallback ignores mode (no offline classification)
    return { scores: getLocalScores(), online: false };
  }
}

async function postScore(name, score, settings) {
  try {
    const res = await fetch(`${API}/api/highscores`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, score, settings }),
    });
    if (!res.ok) throw new Error("bad status");
    const data = await res.json();
    addLocalScore(name, score);
    return { scores: data.scores, standard: !!(data.entry && data.entry.standard) };
  } catch (e) {
    return { scores: addLocalScore(name, score), standard: false };
  }
}

export class Game {
  constructor(screen, io, sound) {
    this.s = screen;
    this.io = io;
    this.sound = sound;
    this.rng = new Rng();
    this.settings = loadSettings();
    this.mapData = null;
    this.prices = { 1: {}, 2: {}, 3: {} };
  }

  // ---------- state helpers ----------
  cargoValue() {
    let t = 0;
    for (let w = 1; w <= 3; w++) t += this.cargo[w] * BASE[w];
    return t;
  }

  totalWeight() {
    return this.cargo[1] + this.cargo[2] + this.cargo[3];
  }

  mostValuableCargo(minCargo) {
    let ware = 0;
    let value = 0;
    for (let w = 1; w <= 3; w++) {
      if (this.cargo[w] * BASE[w] > value && this.cargo[w] >= minCargo) {
        ware = w;
        value = this.cargo[w] * BASE[w];
      }
    }
    return { ware, value };
  }

  saveState(midday = false) {
    saveGame({
      day: this.day,
      money: this.money,
      bank: this.bank,
      capacity: this.capacity,
      damage: this.damage,
      cargo: { ...this.cargo },
      country: this.country,
      storm: this.storm,
      closedRepairDay: this.closedRepairDay,
      lastDayStartEvent: this.lastDayStartEvent,
      rngState: this.rng.getState(),
      settings: { ...this.settings },
      midday,
      timeOfDay: this.timeOfDay,
      prices: midday ? this.prices : null,
    });
  }

  loadState() {
    const d = loadGame();
    if (!d) return false;
    this.day = d.day;
    this.money = d.money;
    this.bank = d.bank;
    this.capacity = d.capacity;
    this.damage = d.damage;
    this.cargo = { 1: d.cargo[1], 2: d.cargo[2], 3: d.cargo[3] };
    this.country = d.country;
    this.storm = d.storm;
    this.closedRepairDay = d.closedRepairDay;
    this.lastDayStartEvent = d.lastDayStartEvent;
    this.rng.setState(d.rngState);
    if (d.settings) this.settings = { ...this.settings, ...d.settings };
    if (d.midday && d.prices) {
      // resume in the middle of a game day, right at the main menu
      this.resumeMidday = true;
      this.timeOfDay = d.timeOfDay;
      this.prices = { 1: { ...d.prices[1] }, 2: { ...d.prices[2] }, 3: { ...d.prices[3] } };
    }
    return true;
  }

  getProfitSnapshot() {
    return {
      country: this.country,
      money: this.money,
      capacity: this.capacity,
      totalWeight: this.totalWeight(),
      prices: {
        1: { ...this.prices[1] },
        2: { ...this.prices[2] },
        3: { ...this.prices[3] },
      },
    };
  }

  // ---------- rendering helpers ----------
  putMapWindow() {
    if (this.mapData) {
      this.s.putData(this.mapData, 0, 0);
      return;
    }
    this.s.putPic("map_win", 0, 142);
    if (this.country !== ISRAEL) {
      // resumed mid-week away from the home port: map_win.png has the ship
      // docked at Israel - relocate it to the current port (a sea sample
      // wipes the original)
      const [ix, iy] = COORD[ISRAEL];
      const [cx, cy] = COORD[this.country];
      const ship = this.s.copyRegion(ix, iy, 19, 15);
      const sea = this.s.copyRegion(96, 28, 19, 15);
      this.s.drawCanvas(sea, ix, iy);
      this.s.drawCanvas(ship, Math.min(221, cx), Math.min(127, cy));
      this.mapData = this.s.getData(0, 0, 240, 142);
    }
  }

  msgDraw(l1, l2, l3) {
    this.preMsg = this.s.getData(10, 109, 231, 138);
    this.s.putPic("message_sgn", 10, 138);
    const lines = [l1, l2, l3];
    for (let i = 0; i < 3; i++) {
      const v = vis(lines[i] || "");
      if (v.length) this.s.writeAt(29 - v.length, 15 + i, v);
    }
  }

  // ShowMessage('W', ...) - draw, wait for tap, restore background
  async showMessage(l1, l2, l3, opts = {}) {
    this.msgDraw(l1, l2, l3);
    if (opts.beep) this.sound.errorBeep();
    await this.io.waitKey();
    this.s.putData(this.preMsg, 10, 109);
  }

  // ShowMessage('N', ...) - draw only
  showMessageNow(l1, l2, l3, opts = {}) {
    this.msgDraw(l1, l2, l3);
    if (opts.beep) this.sound.errorBeep();
  }

  showHUD() {
    const s = this.s;
    s.writeAt(33, 1, vis(COUNTRY[this.country]).padStart(6));
    s.writeAt(32, 3, vis(DAYS[this.day] || "").padStart(5));
    s.writeAt(32, 5, String(this.timeOfDay).padStart(2) + ":00");
    s.writeAt(32, 17, " ".repeat(8));
    let mcol = 34;
    if (this.money >= 10000) mcol = 33;
    if (this.money >= 100000) mcol = 32;
    s.writeAt(mcol, 17, commas(this.money).padStart(5));
    s.writeAt(32, 9, "   ");
    s.writeAt(32, 9, wn(this.cargo[COPPER], 3));
    s.writeAt(32, 11, "    ");
    s.writeAt(32, 11, wn(this.cargo[OLIVES], 3));
    s.writeAt(32, 13, "     ");
    s.writeAt(32, 13, wn(this.cargo[WHEAT], 3));
    const cols = { 1: 30, 2: 16, 3: 2 };
    for (let c = 1; c <= 3; c++) {
      s.writeAt(cols[c], 21, wn(this.prices[c][COPPER], 4));
      s.writeAt(cols[c], 23, wn(this.prices[c][OLIVES], 4));
      s.writeAt(cols[c], 25, wn(this.prices[c][WHEAT], 4));
    }
    if (this.storm) {
      const px = { 3: 4, 2: 111, 1: 217 }[this.storm];
      s.writePx(px, 147, vis("סערה"), 2);
    }
    s.writePx(244, 50, String(this.capacity).padStart(3));
  }

  renderHelp() {
    const s = this.s;
    s.putPic("help_scr", 0, 199);
    s.writeAt(9, 7, (this.storm ? vis(`סערה באזור ${COUNTRY[this.storm]}`) : vis("נאה")).padStart(17));
    s.writeAt(9, 12, String(Math.round(this.bank)).padStart(17));
    s.writeAt(9, 17, vis(`${this.capacity} טון`).padStart(17));
    s.writeAt(9, 22, (this.damage <= 0 ? vis("תקין") : vis(`נזק של ${Math.round(this.damage)} שקל`)).padStart(17));
  }

  toggleCountryHighlight(country) {
    const ranges = { 1: [256, 303], 2: [147, 190], 3: [44, 86] };
    const [x1, x2] = ranges[country];
    this.s.invertRect(x1, 146, x2, 155);
  }

  // ---------- input helpers ----------
  async inputReal(col, row, maxDigits, min, max, quick = []) {
    for (;;) {
      const v = await this.io.inputNumber({ col, row, maxDigits, quick });
      if (v === -1) return -1;
      if (!(min === 0 && max === 0) && (v > max || v < min)) {
        await this.showMessage("", `נא הכנס מספר בין ${min} ל-${max}`, "", { beep: true });
        this.s.writeAt(col, row, " ".repeat(maxDigits));
        continue;
      }
      return v;
    }
  }

  async cannotAfford(amount) {
    if (amount > this.money) {
      await this.showMessage("", "אין ברשותך מספיק כסף!", "", { beep: true });
      return true;
    }
    return false;
  }

  // ---------- bad outcomes ----------
  applyBadOutcome(type) {
    const rng = this.rng;
    if (type === "cargo") {
      for (let w = 1; w <= 3; w++) {
        if (this.cargo[w] > 2) {
          let lost = Math.trunc(((20 + rng.random(21)) / 100) * this.cargo[w]);
          if (lost < 1) lost = 1;
          this.cargo[w] -= lost;
          return { ware: w, amount: lost };
        }
      }
      return { ware: 0, amount: 0 };
    }
    if (type === "sinking") {
      if (this.totalWeight() <= 3 * this.capacity) return this.applyBadOutcome("cargo");
      let w = COPPER;
      if (this.cargo[OLIVES] > this.cargo[COPPER]) w = OLIVES;
      if (this.cargo[WHEAT] > this.cargo[w]) w = WHEAT;
      let lost = this.totalWeight() - 3 * this.capacity;
      if (lost >= this.cargo[w]) {
        lost = this.cargo[w];
        this.cargo[w] = 0;
      } else {
        this.cargo[w] -= lost;
        const extra = this.applyBadOutcome("cargo");
        if (extra.ware === w) lost += extra.amount;
        else if (extra.ware > 0) this.cargo[extra.ware] += extra.amount;
      }
      return { ware: w, amount: lost };
    }
    if (type === "money") {
      const lost = 10 * Math.trunc(this.money / (rng.random(40) + 40));
      this.money -= lost;
      return { ware: 0, amount: lost };
    }
    // damage
    let lost = 10 * Math.trunc((this.money + this.cargoValue()) / (rng.random(30) + 50));
    if (lost < 100) lost = 100;
    this.damage += lost;
    return { ware: 0, amount: lost };
  }

  // ---------- offers (crew / pirates) ----------
  async offerFlow() {
    const offered = { 1: 0, 2: 0, 3: 0 };
    let offeredMoney = 0;
    for (let w = 1; w <= 3; w++) {
      if (this.cargo[w] > 0) {
        for (;;) {
          this.showMessageNow(`כמה טון ${WARE[w]} אתה מוכן לתת?`, "", "");
          let v;
          do {
            v = Math.trunc(await this.inputReal(20, 16, 5, 0, MAX_INT));
          } while (v < 0);
          if (v <= this.cargo[w]) {
            offered[w] = v;
            break;
          }
          await this.showMessage("", "אין ברשותך סכום זה!", "", { beep: true });
        }
      }
    }
    if (this.money > this.cargoValue() / 10) {
      for (;;) {
        this.showMessageNow("כמה כסף אתה מוכן לתת?", "", "");
        let v;
        do {
          v = await this.inputReal(20, 16, 8, 0, 0);
        } while (v < 0);
        if (v <= this.money) {
          offeredMoney = v;
          break;
        }
        await this.showMessage("", "אין ברשותך סכום זה!", "", { beep: true });
      }
    }
    const total = offered[1] * BASE[1] + offered[2] * BASE[2] + offered[3] * BASE[3] + offeredMoney;
    const threshold = (this.cargoValue() + this.money) / (this.rng.random(6) + 4);
    if (total > threshold) {
      this.money -= offeredMoney;
      for (let w = 1; w <= 3; w++) this.cargo[w] -= offered[w];
      return true;
    }
    return false;
  }

  // ---------- settings screen ----------
  // The original startup settings menu was replaced by the modern React
  // settings overlay (src/Settings.js), reachable from the title screen
  // and the in-game topbar.

  // ---------- new day ----------
  generateNewPrices() {
    for (const c of [2, 1, 3]) {
      this.prices[c][COPPER] = BASE[COPPER] - 500 + this.rng.random(11) * 100;
      this.prices[c][OLIVES] = BASE[OLIVES] - 150 + this.rng.random(7) * 50;
      this.prices[c][WHEAT] = BASE[WHEAT] - 15 + this.rng.random(8) * 5;
    }
  }

  updateStorm() {
    if (this.rng.random(8) < 3 && this.day !== 1) this.storm = this.rng.random(3) + 1;
    else this.storm = 0;
  }

  async showNewDayScreen() {
    this.s.putPic("newday_scr", 0, 199);
    this.s.putPic(`day${this.day}_sgn`, 165, 89);
    await this.io.waitKey();
  }

  async showStormScreen() {
    if (!this.storm) {
      this.s.putPic("goodwthr_scr", 0, 199);
    } else {
      this.s.putPic("badwthr_scr", 0, 199);
      this.s.writeAt(28, 20, vis(COUNTRY[this.storm]).padStart(6));
    }
    await this.io.waitKey();
  }

  sameEventGroup(a, b) {
    if (a === b) return true;
    const groups = [
      [1, 2], [3, 4], [5, 6], [7, 8], [9, 10], [11, 12],
      [13, 18], [19, 20], [21, 22], [23, 26], [27, 28], [29, 30],
    ];
    return groups.some(([lo, hi]) => a >= lo && a <= hi && b >= lo && b <= hi);
  }

  async updateSpecialPrices(up, country, newPrice, ware) {
    this.prices[country][ware] = newPrice;
    const row = up ? 2 : 23;
    this.s.writeAt(7, row, vis(`מחיר ה${WARE[ware]} ב${COUNTRY[country]}`).padStart(27));
    this.s.writeAt(7, row + 1, vis(`${up ? "קפץ ל-" : "ירד ל-"}${newPrice} שקלים!!`).padStart(25));
    await this.io.waitKey();
  }

  async merchantBuysAll(w, price) {
    const s = this.s;
    s.putPic("socher_scr", 0, 199);
    const lines = [
      "סוחר זקוק בדחיפות לסחורה.",
      `הוא מוכן להציע לך ${price} ש"ח`,
      `לכל טון של ${WARE[w]}, אם תמכור לו`,
      `את ${this.cargo[w]} הטון שלך.`,
    ];
    lines.forEach((l, i) => s.writeAt(3, 2 + i, vis(l).padStart(34)));
    s.writeAt(3, 7, vis("האם אתה מעונין למכור לו ? (כ/ל)").padStart(34));
    if (await this.io.yesNo({ col: 3, row: 7 })) {
      this.money += price * this.cargo[w];
      this.cargo[w] = 0;
      s.writeAt(20, 9, vis("נמכר !!!"));
      await this.io.waitKey();
    }
  }

  // returns true when the day should be skipped (crew events)
  async doDayStartEvent() {
    const rng = this.rng;
    const s = this.s;
    let skip = false;
    let ev = 0;
    let happened;
    do {
      happened = true;
      do {
        ev = rng.random(30) + 1;
      } while (this.sameEventGroup(ev, this.lastDayStartEvent));
      if (this.money > 20000 && rng.random(6) === 1) ev = 15;
      if (this.money > 45000 && rng.random(6) === 1) ev = 15;

      if (ev <= 2) {
        s.putPic("copperup_scr", 0, 199);
        await this.updateSpecialPrices(true, rng.random(3) + 1, BASE[COPPER] + 1100 + 100 * rng.random(5), COPPER);
      } else if (ev <= 4) {
        s.putPic("olivesup_scr", 0, 199);
        await this.updateSpecialPrices(true, rng.random(3) + 1, BASE[OLIVES] + 200 + 50 * rng.random(4), OLIVES);
      } else if (ev <= 6) {
        s.putPic("wheatup_scr", 0, 199);
        await this.updateSpecialPrices(true, rng.random(3) + 1, BASE[WHEAT] + 20 + rng.random(5) * 5, WHEAT);
      } else if (ev <= 8) {
        s.putPic("copperdn_scr", 0, 199);
        await this.updateSpecialPrices(false, rng.random(3) + 1, BASE[COPPER] - 1100 - 100 * rng.random(5), COPPER);
      } else if (ev <= 10) {
        s.putPic("olivesdn_scr", 0, 199);
        await this.updateSpecialPrices(false, rng.random(3) + 1, BASE[OLIVES] - 150 - rng.random(3) * 50, OLIVES);
      } else if (ev <= 12) {
        s.putPic("wheatdn_scr", 0, 199);
        await this.updateSpecialPrices(false, rng.random(3) + 1, BASE[WHEAT] - 15 - rng.random(3) * 5, WHEAT);
      } else if (ev <= 18) {
        happened = await this.expandShipEvent();
      } else if (ev <= 20) {
        if (this.day < 6 && this.settings.crewStrike) skip = await this.crewEvent();
        else happened = false;
      } else if (ev <= 22) {
        let { amount } = this.applyBadOutcome("money");
        if (amount > 0) {
          s.putPic("thieves_scr", 0, 199);
          s.writeAt(14, 24, vis(`${amount} שקל.`).padStart(15));
        } else {
          const r = this.applyBadOutcome("cargo");
          if (r.amount > 0) {
            s.putPic("thieves_scr", 0, 199);
            s.writeAt(14, 24, vis(`${r.amount} טון ${WARE[r.ware]}.`).padStart(15));
            amount = r.amount;
          } else happened = false;
        }
        if (happened) await this.io.waitKey();
      } else if (ev <= 26) {
        if (this.cargo[COPPER] > 1) {
          await this.merchantBuysAll(COPPER, this.prices[ISRAEL][COPPER] + 50 * rng.random(30) - 600);
        } else if (this.cargo[OLIVES] > 1) {
          await this.merchantBuysAll(OLIVES, this.prices[ISRAEL][OLIVES] + 10 * rng.random(70) - 300);
        } else if (this.cargo[WHEAT] > 1) {
          await this.merchantBuysAll(WHEAT, this.prices[ISRAEL][WHEAT] + 5 * rng.random(14) - 40);
        } else happened = false;
      } else if (ev <= 28) {
        happened = await this.merchantSellsEvent();
      } else {
        const dmg = 10 * Math.trunc((this.cargoValue() + this.money) / (rng.random(80) + 150));
        if (dmg > 0) {
          s.putPic("fishing_scr", 0, 199);
          this.damage += dmg;
          s.writeAt(20, 3, wn(dmg, 4));
          await this.io.waitKey();
        } else happened = false;
      }
    } while (!happened);
    this.lastDayStartEvent = ev;
    return skip;
  }

  async expandShipEvent() {
    const rng = this.rng;
    const s = this.s;
    const costs = { 1: 0, 2: 0, 3: 0 };
    let moneyCost = 0;
    let bankCost = 0;
    let ware = 0;
    const amount = 50 * (rng.random(2) + 1);
    const price = 10 * (rng.random(amount) + 2 * amount);
    if (price > this.money + this.bank + this.cargoValue()) return false;
    s.putPic("expand_scr", 0, 199);
    s.writeAt(29, 14, wn(amount, 3));
    if (this.money > price) moneyCost = price;
    else if (this.bank > price) bankCost = price;
    else if (this.money + this.bank > price) {
      moneyCost = Math.trunc(this.money);
      bankCost = price - moneyCost;
    } else {
      const mv = this.mostValuableCargo(1);
      ware = mv.ware;
      if (mv.value >= price) {
        costs[ware] = Math.round(price / BASE[ware]);
      } else {
        costs[ware] = this.cargo[ware];
        if (this.money > this.bank || this.money >= moneyCost) {
          moneyCost = price - Math.trunc(mv.value);
          if (moneyCost > this.money) moneyCost = Math.trunc(this.money);
        } else {
          bankCost = price - Math.trunc(mv.value);
          if (bankCost > this.bank) bankCost = Math.trunc(this.bank);
        }
      }
    }
    if (moneyCost + bankCost > 0) s.writeAt(18, 15, vis(`${moneyCost + bankCost} ש"ח`));
    if (ware > 0 && costs[ware] > 0) {
      let str = `${costs[ware]} טון ${WARE[ware]}.`;
      if (moneyCost + bankCost > 0) str = `ובנוסף ${str}`;
      s.writeAt(15, 16, vis(str).padStart(22));
    }
    if (await this.io.yesNo({ col: 18, row: 18 })) {
      this.capacity += amount;
      this.money -= moneyCost;
      this.bank -= bankCost;
      for (let w = 1; w <= 3; w++) this.cargo[w] -= costs[w];
      s.putPic("message_sgn", 90, 194);
      s.writeAt(18, 23, vis("ההרחבה  בוצעה."));
      await this.io.waitKey();
      if (bankCost > 0) {
        if (moneyCost > 0) await this.showMessage(`${bankCost} שקל נלקחו מחשבונך`, "בבנק.", "");
        else await this.showMessage("הכסף נלקח מחשבונך בבנק.", "", "");
      }
    }
    return true;
  }

  async crewEvent() {
    const s = this.s;
    s.putPic("crew_scr", 0, 199);
    s.writeAt(7, 3, wn(this.cargo[COPPER], 5));
    s.writeAt(7, 4, wn(this.cargo[OLIVES], 5));
    s.writeAt(7, 5, wn(this.cargo[WHEAT], 5));
    s.writeAt(7, 6, String(Math.round(this.money)).padStart(5));
    const rects = [
      { x1: 54, y1: 177, x2: 198, y2: 183 },
      { x1: 78, y1: 186, x2: 198, y2: 192 },
    ];
    let opt;
    do {
      opt = await this.io.chooseOption(rects, {
        escapable: false,
        labels: ["צוות חדש", "להציע פשרה"],
      });
    } while (opt <= 0);
    if (opt === 1) {
      await this.showMessage("למחרת...", "אתה עובד עם צוות חדש.", "");
      return true;
    }
    if (await this.offerFlow()) {
      await this.showMessage("הצוות קיבל את הצעתך.", "", "");
      return false;
    }
    await this.showMessage("הצוות סרב להצעה.", "עברו יומיים ואתה מוצא", "צוות חדש");
    this.day += 1;
    return true;
  }

  async merchantSellsEvent() {
    const rng = this.rng;
    const s = this.s;
    const w = rng.random(3) + 1;
    const price = 5 * (Math.trunc(BASE[w] / 10) + Math.trunc(rng.random(BASE[w]) / 5));
    const supplyBase = rng.random(Math.trunc(0.75 * this.money) + 1) + 0.25 * this.money;
    const supply = 5 * Math.trunc(supplyBase / BASE[w] / 5 + 1);
    if (!(price * supply <= this.money && this.money > 0)) return false;
    s.putPic("socher_scr", 0, 199);
    const supStr = supply === 1 ? "" : `${supply} `;
    s.writeAt(3, 2, vis(`סוחר מעונין למכור לך ${supStr}טון ${WARE[w]}`).padStart(34));
    s.writeAt(3, 4, vis(`במחיר ${price} שקל לטון`).padStart(34));
    s.writeAt(3, 6, vis("האם אתה מעונין לקנות ? (כ/ל)").padStart(34));
    if (await this.io.yesNo({ col: 3, row: 6 })) {
      this.money -= price * supply;
      this.cargo[w] += supply;
      s.writeAt(18, 7, vis("קנית !!!"));
      await this.io.waitKey();
    }
    return true;
  }

  // ---------- trade windows ----------
  async showBuyWindow() {
    const s = this.s;
    const p = this.prices[this.country];
    if (this.money < p[WHEAT]) {
      await this.showMessage("כספך אינו מאפשר לך", "קנית סחורות", "");
      return;
    }
    const rectsByCountry = {
      [TURKEY]: {
        win: "buyturk_win",
        rects: [
          { x1: 177, y1: 2, x2: 236, y2: 18 },
          { x1: 72, y1: 2, x2: 131, y2: 18 },
          { x1: 180, y1: 65, x2: 237, y2: 80 },
        ],
      },
      [ISRAEL]: {
        win: "buyisr_win",
        rects: [
          { x1: 177, y1: 2, x2: 236, y2: 18 },
          { x1: 8, y1: 2, x2: 67, y2: 18 },
          { x1: 179, y1: 86, x2: 236, y2: 101 },
        ],
      },
      [EGYPT]: {
        win: "buyegpt_win",
        rects: [
          { x1: 177, y1: 2, x2: 236, y2: 18 },
          { x1: 177, y1: 33, x2: 236, y2: 49 },
          { x1: 179, y1: 86, x2: 236, y2: 101 },
        ],
      },
    };
    const cfg = rectsByCountry[this.country];
    s.putPic(cfg.win, 1, 142);
    const w = await this.io.chooseOption(cfg.rects, {
      escapable: true,
      restore: false,
      labels: [WARE[1], WARE[2], WARE[3]],
    });
    if (w <= 0) return;
    const price = p[w];
    let amount = 0;
    if (this.money < price) {
      await this.showMessage("כספך אינו מאפשר לך", `לקנות ${WARE[w]}`, "");
    } else {
      for (;;) {
        s.putPic("buy_sgn", 22, 138);
        const maxAff = Math.min(Math.trunc(this.money / price), MAX_INT);
        s.writeAt(6, 15, String(maxAff));
        let qB = this.capacity - this.totalWeight();
        qB = Math.max(0, Math.min(qB, maxAff));
        // reserve enough money for the configured number of guard ships;
        // 0 ships falls back to the classic 10%/500 reserve
        const nGuards = Math.trunc(this.settings.guardReserve || 0);
        const reserve =
          nGuards > 0 ? nGuards * this.estGuardPrice() : Math.max(Math.trunc(this.money / 10), 500);
        let qC = Math.trunc((this.money - reserve) / price);
        qC = Math.max(0, Math.min(qC, MAX_INT));
        amount = await this.inputReal(6, 17, 5, 0, MAX_INT, [
          { label: "הכל", value: maxAff },
          { label: "קיבולת", value: qB },
          { label: "שמור רזרבה", value: qC },
        ]);
        if (!(await this.cannotAfford(amount * price))) break;
      }
    }
    if (amount > 0) {
      this.money -= amount * price;
      this.cargo[w] += Math.trunc(amount);
    }
  }

  async showSellWindow() {
    const s = this.s;
    const p = this.prices[this.country];
    if (this.cargo[1] === 0 && this.cargo[2] === 0 && this.cargo[3] === 0) {
      await this.showMessage(" ", "אין  לך  סחורה  למכור", " ");
      return;
    }
    const wins = { [TURKEY]: "sellturk_win", [ISRAEL]: "sellisr_win", [EGYPT]: "sellegpt_win" };
    s.putPic(wins[this.country], 0, 142);
    const egypt = this.country === EGYPT;
    const rects = [
      { x1: 19, y1: 26, x2: egypt ? 58 : 59, y2: egypt ? 34 : 35 },
      { x1: 19, y1: 42, x2: egypt ? 57 : 59, y2: egypt ? 50 : 51 },
      { x1: 19, y1: 58, x2: egypt ? 57 : 59, y2: egypt ? 66 : 67 },
    ];
    const w = await this.io.chooseOption(rects, {
      escapable: true,
      restore: false,
      actions: [{ label: "מכור הכל", value: "sell_all" }],
      labels: [WARE[1], WARE[2], WARE[3]],
    });
    if (w === "sell_all") {
      let gain = 0;
      for (let i = 1; i <= 3; i++) {
        gain += this.cargo[i] * p[i];
        this.cargo[i] = 0;
      }
      this.money += gain;
      await this.showMessage("כל הסחורה נמכרה", `תמורת ${commas(gain)} ש"ח`, "");
      return;
    }
    if (w <= 0) return;
    let amount = 0;
    if (this.cargo[w] > 0) {
      for (;;) {
        s.putPic("sell_sgn", 120, 60);
        amount = await this.inputReal(21, 7, 5, 0, MAX_INT, [{ label: "הכל", value: this.cargo[w] }]);
        if (amount <= this.cargo[w]) break;
        await this.showMessage("אין לך כל-כך הרבה", `${WARE[w]} למכור`, "", { beep: true });
      }
    } else {
      await this.showMessage(`אין לך ${WARE[w]} למכור`, "", "", { beep: true });
      amount = 0;
    }
    if (amount === -1) amount = 0;
    this.money += amount * p[w];
    this.cargo[w] -= Math.trunc(amount);
  }

  async showBankWindow() {
    const s = this.s;
    s.putPic("bank_win", 0, 142);
    if (this.settings.bankInterest) s.writeAt(4, 17, vis("ריבית לילה 10%"));
    const rects = [
      { x1: 97, y1: 7, x2: 149, y2: 16 },
      { x1: 16, y1: 7, x2: 64, y2: 16 },
    ];
    const opt = await this.io.chooseOption(rects, {
      escapable: true,
      restore: false,
      labels: ["הפקדה", "משיכה"],
    });
    if (opt === 1) {
      if (this.money <= 0) {
        await this.showMessage(" ", "אין לך כסף להפקיד !", " ", { beep: true });
        return;
      }
      for (;;) {
        s.putPic("deposit_sgn", 11, 139);
        // pocket reserve so the player can still afford guard ships after a deposit
        const nGuards = Math.trunc(this.settings.guardReserve || 0);
        const reserve =
          nGuards > 0 ? nGuards * this.estGuardPrice() : Math.max(Math.trunc(this.money / 10), 500);
        const reserveDeposit = Math.max(0, Math.trunc(this.money - reserve));
        const amt = await this.inputReal(5, 17, 8, 0, 0, [
          { label: "הכל", value: Math.trunc(this.money) },
          { label: "שמור רזרבה", value: reserveDeposit },
        ]);
        if (amt <= 0) return;
        if (amt <= this.money) {
          this.bank += amt;
          this.money -= amt;
          await this.showMessage(" ", "פקיד הבנק קיבל את הכסף", " ");
          return;
        }
        await this.showMessage(" ", "אין לך כל כך הרבה כסף !", " ", { beep: true });
      }
    } else if (opt === 2) {
      if (this.bank <= 0) {
        await this.showMessage(" ", "אין לך כסף בבנק !", " ", { beep: true });
        return;
      }
      for (;;) {
        s.putPic("withrow_sgn", 11, 139);
        const amt = await this.inputReal(5, 17, 8, 0, 0, [{ label: "הכל", value: Math.trunc(this.bank) }]);
        if (amt < 0) return;
        if (amt <= this.bank) {
          this.bank -= amt;
          this.money += amt;
          if (amt !== 0) await this.showMessage(" ", "קיבלת את הכסף", " ");
          return;
        }
        await this.showMessage(" ", "אין לך סכום כסף כזה בבנק !", " ", { beep: true });
      }
    }
  }

  async showRepairWindow() {
    const s = this.s;
    let toRepair;
    for (;;) {
      s.putPic("repair_win", 0, 142);
      s.writeAt(21, 17, String(Math.round(this.damage)).padStart(4));
      const qA = Math.min(Math.trunc(this.damage), Math.trunc(this.money));
      toRepair = await this.inputReal(5, 17, 5, 0, 0, [{ label: "הכל", value: qA }]);
      if (toRepair <= 0 || toRepair <= this.money) break;
      await this.showMessage("", "אין לך כל-כך הרבה כסף", "", { beep: true });
    }
    if (toRepair >= 0) {
      this.money -= toRepair;
      this.damage -= toRepair;
      if (this.damage < 0) this.damage = 0;
      if (this.damage === 0) await this.showMessage("", "הספינה כעת תקינה לחלוטין", "");
      else await this.showMessage("", `נשאר נזק בסך ${Math.round(this.damage)} ש"ח`, "");
    }
  }

  isUnableToRepair() {
    return this.damage > this.money + this.bank + this.cargoValue();
  }

  // ---------- travel ----------
  async animateTravel(from, to) {
    const s = this.s;
    let mcol = 34;
    if (this.money > 10000) mcol = 33;
    if (this.money > 100000) mcol = 32;
    s.writeAt(mcol, 17, commas(this.money).padStart(5));
    this.putMapWindow();
    const [fx, fy] = from;
    const [tx, ty] = to;
    // Original CODE.PAS behavior: grab the ship patch (ship + sea margin) and
    // stamp it along the route. Each ~1px step overlaps the previous stamp,
    // so the sea margin wipes the trail - no ghost ship is left behind.
    const patch = s.copyRegion(fx, fy, 19, 15);
    const total = (this.travelEnd - this.timeOfDay) * 60;
    let minute = 0;
    for (let i = 1; i <= total; i++) {
      minute += 1;
      if (minute < 60) {
        if (minute % 15 === 0) s.writeAt(35, 5, String(minute).padStart(2, "0"));
      } else {
        minute = 0;
        this.timeOfDay += 1;
        s.writeAt(32, 5, String(this.timeOfDay).padStart(2) + ":00");
      }
      const x = Math.round(fx + (i / total) * (tx - fx));
      const yTop = Math.round(fy + (i / total) * (ty - fy));
      s.drawCanvas(patch, Math.max(0, Math.min(221, x)), Math.max(0, Math.min(127, yTop)));
      await sleep(10);
    }
    s.drawCanvas(patch, Math.max(0, Math.min(221, tx)), Math.max(0, Math.min(127, ty)));
    this.mapData = s.getData(0, 0, 240, 142);
  }

  shouldDoHighRiskEvent(dmg, risk) {
    return (
      this.rng.random(1 + Math.trunc((dmg + 399) / 400) + 2 * risk) > 0 &&
      this.rng.random(2) === 1
    );
  }

  isShipSinking(weight) {
    let chance = 30000;
    if (weight > this.capacity + 70) chance = 1;
    else if (weight > this.capacity + 60) chance = 2;
    else if (weight > this.capacity + 40) chance = 3;
    else if (weight > this.capacity + 20) chance = 4;
    else if (weight > this.capacity) chance = 6;
    return this.rng.random(chance) === 0;
  }

  async changeTravelDestination() {
    this.s.putPic("change_win", 0, 142);
    const orig = this.travelDest;
    this.travelDest = this.travelDest === ISRAEL ? TURKEY : ISRAEL;
    await sleep(1500);
    await this.showMessage(
      "כתוצאה מרוחות חזקות",
      "וטעות בניווט, הגעת",
      `ל${COUNTRY[this.travelDest]} במקום ל${COUNTRY[orig]}`
    );
    if (this.travelStart === EGYPT && this.travelDest === TURKEY) this.travelEnd += 4;
  }

  async applyConfrontDamage(chance) {
    if (this.rng.random(chance) === 0) {
      const lost = 10 * Math.trunc(((this.rng.random(15) + 5) / 1000) * (this.money + this.cargoValue()));
      this.damage += lost;
      if (chance === 2) await this.showMessage("בנוסף, נגרם", "לספינה נזק בשווי", `${lost} שקלים.`);
      else await this.showMessage("לרוע מזלך, ספינתך", "נפגעה תוך כדי הקרב.", `שווי הנזק ${lost} ש"ח.`);
    }
  }

  async confrontPirates(guard, adj) {
    const rng = this.rng;
    this.s.putPic("confront_win", 0, 142);
    const difficulty = rng.random(4);
    if (difficulty < guard - adj || (guard - adj <= 0 && rng.random(6) === 1)) {
      await this.showMessage("לאחר קרב ממושך -", "הבסת את הפיראטים !", "");
      if (rng.random(3) === 1) {
        this.capacity += 50;
        await this.showMessage("לקחת בשבי את ספינת", "שודדי הים, וקיבולת הספינה", "שלך גדלה ב-50 טון.");
      } else {
        guard += adj;
        let booty =
          Math.trunc((this.money + this.cargoValue() + this.bank) / 100) * (rng.random(15) + 5) +
          (guard * (this.money + this.cargoValue() + this.bank)) / 10;
        if (booty < 100) booty = 100;
        booty = 5 * Math.trunc(booty / 5);
        await this.showMessage("מצאת מטמון של", `${Math.trunc(booty)} שקל על ספינתם`, "");
        this.money += booty;
      }
      await this.applyConfrontDamage(4);
    } else {
      if (guard === 0) await this.showMessage("ללא הגנת ספינות משמר,", "אתה הובסת על ידי הפיראטים", "");
      else if (guard === 1) await this.showMessage("הפיראטים התגברו על ספינת", "המשמר שלך", "");
      else await this.showMessage("הפיראטים ניצחו את", `${guard} ספינות המשמר שלך`, "");
      guard += adj;
      if (guard > 3) guard = 3;
      const mv = this.mostValuableCargo(5);
      if (mv.value > this.money) {
        let lost = ((rng.random(15) + 5) * this.cargo[mv.ware]) / 100;
        if (guard < 3) lost += Math.trunc(((3 - guard) * this.cargo[mv.ware]) / 10);
        if (lost < 1) lost = 1;
        this.cargo[mv.ware] -= Math.trunc(lost);
        await this.showMessage("אחרי קרב קשה     ", "גנבו ממך שודדי הים", `${Math.trunc(lost)} טון ${WARE[mv.ware]}`);
        await this.applyConfrontDamage(2);
      } else if (this.money > 500) {
        let lost = 10 * Math.trunc(rng.random(Math.trunc(0.015 * this.money)) + this.money / 200);
        if (guard < 3) lost += (3 - guard) * Math.trunc(this.money / 10);
        lost = Math.trunc(lost);
        this.money -= lost;
        await this.showMessage(`הם גנבו ממך ${lost} שקלים`, "", "");
        await this.applyConfrontDamage(2);
      } else {
        let lost =
          Math.trunc(((rng.random(15) + 35 - 10 * guard) / 1000) * (this.cargoValue() + this.money + this.bank)) * 10;
        if (lost < 100) lost = 100;
        this.damage += lost;
        await this.showMessage("לרוע מזלך, ספינתך", "נפגעה תוך כדי הקרב.", `שווי הנזק ${lost} ש"ח`);
      }
    }
    this.showHUD();
  }

  async piratesFlow(guard, weight) {
    const s = this.s;
    s.putPic("pirates_win", 0, 142);
    await this.io.waitKey();
    s.putPic("pirates_sgn", 4, 139);
    s.writeAt(18, 17, wn(guard, 2));
    const rects = [
      { x1: 108, y1: 117, x2: 133, y2: 123 },
      { x1: 72, y1: 117, x2: 96, y2: 123 },
      { x1: 7, y1: 117, x2: 58, y2: 123 },
    ];
    const opt = await this.io.chooseOption(rects, {
      escapable: false,
      labels: ["לתקוף", "לברוח", "להציע פשרה"],
    });
    if (opt === 1) {
      await this.confrontPirates(guard, 0);
    } else if (opt === 2) {
      s.putPic("runaway_win", 0, 142);
      let runCtl;
      if (weight > 0.81 * this.capacity) runCtl = 0;
      else {
        let chance = Math.trunc(this.capacity / 30) - Math.trunc(weight / 30) + 1;
        if (chance < 1) chance = 1;
        runCtl = this.rng.random(chance);
      }
      let dmgCtl = 0;
      if (runCtl > 0) dmgCtl = this.rng.random(Math.trunc(this.damage / 30) + 1);
      if (dmgCtl > 0) runCtl = 0;
      if (runCtl === 0) {
        if (weight > 0.5 * this.capacity) await this.showMessage("ספינתך כבדה מדי, ולא", "הצליחה לברוח", "");
        else if (this.damage > 0) await this.showMessage("לא הצלחת לברוח עקב", "הנזק שבספינתך", "");
        else await this.showMessage("ספינת הפיראטים המהירה הצליחה", "להשיג את ספינתך", "");
        await this.confrontPirates(guard, 1);
      } else {
        await this.showMessage("", "הצלחת  להמלט  מהם  ! !", "");
      }
    } else if (opt === 3) {
      s.putPic("offer_win", 0, 142);
      if (await this.offerFlow()) {
        await this.showMessage("שודדי הים החליטו לקבל", "את הצעתך", "");
        this.showHUD();
      } else {
        await this.showMessage("שודדי הים דוחים את", "הצעתך ותוקפים אותך !", "");
        await this.confrontPirates(guard, 1);
      }
    }
  }

  async doTravelEvent(curWaypoint, dmgArg, weightArg, durArg, riskArg) {
    const rng = this.rng;
    this.hadTravelEvent = false;
    this.didArriveSafely = false;
    if (this.shouldDoHighRiskEvent(dmgArg, riskArg)) {
      const isShipDamaged = dmgArg > 0;
      const forced = !isShipDamaged && rng.random(3) === 1;
      if (isShipDamaged || forced) {
        await this.showMessage("", "ה ס פ י נ ה   נ ע צ ר ה", "");
        if (this.storm === this.country || this.storm === this.travelDest) {
          this.s.putPic("storm_win", 0, 142);
          await this.io.waitKey();
          if (this.cargoValue() > 10000) {
            const r = this.applyBadOutcome("cargo");
            await this.showMessage("הגלים הגבוהים שטפו מספינתך", `${r.amount} טון ${WARE[r.ware] || ""}.`, "");
            this.showHUD();
          } else {
            const r = this.applyBadOutcome("damage");
            await this.showMessage("עקב הסערה נגרם לספינתך", `נזק בשווי ${r.amount} ש"ח.`, "");
          }
        } else {
          const r = this.applyBadOutcome("damage");
          if (this.timeOfDay > 16) {
            this.s.putPic("sirton_win", 0, 142);
            await this.showMessage("עקב תנאי הראות הגרועים", "בלילה - עלית על שרטון.", "");
            await this.showMessage("נגרם לספינתך נזק", `בשווי ${r.amount} ש"ח.`, "");
          } else {
            this.s.putPic("damaged_win", 0, 142);
            if (isShipDamaged)
              await this.showMessage("הנסיעה בספינתך הניזוקה", `החמירה את הנזק ב-${r.amount}`, "שקל נוספים.");
            else await this.showMessage("נגרם לספינתך נזק", `בשווי ${r.amount} ש"ח.`, "");
          }
        }
      } else if (this.settings.navError) {
        await this.changeTravelDestination();
      }
      this.hadTravelEvent = true;
    }
    if (!this.hadTravelEvent && weightArg > this.capacity) {
      if (this.isShipSinking(weightArg)) {
        await this.showMessage("", "ה ס פ י נ ה   נ ע צ ר ה", "");
        this.s.putPic("sinking_win", 0, 142);
        await this.io.waitKey();
        const r = this.applyBadOutcome("sinking");
        await this.showMessage("נאלצת להשליך לים", `${r.amount} טון ${WARE[r.ware] || ""}`, "");
        this.showHUD();
        this.hadTravelEvent = true;
      }
    }
    if (!this.hadTravelEvent && this.timeOfDay + durArg + Math.trunc(durArg / 2) <= 20) {
      if (this.settings.closedPorts && rng.random(12) === 0) {
        const origDest = this.travelDest;
        const wpCoord = COORD[curWaypoint];
        await this.animateTravel(wpCoord, COORD[origDest]);
        if (rng.random(2) === 1) {
          await this.showMessage(`נמל ${COUNTRY[origDest]} סגור זמנית`, "עקב ערפל כבד", "");
        } else {
          this.s.putPic("strike_win", 0, 142);
          await this.io.waitKey();
          await this.showMessage("בשל שביתה ושיבושים", `בעבודה, נמל ${COUNTRY[origDest]}`, "סגור ");
        }
        await this.showMessage("", `עליך לחזור ל${COUNTRY[this.travelStart]}`, "");
        this.travelDest = this.travelStart;
        this.travelEnd = this.timeOfDay + Math.trunc(durArg / 2);
        await this.animateTravel(COORD[origDest], wpCoord);
        this.travelEnd = this.timeOfDay + Math.trunc(durArg / 2);
        this.hadTravelEvent = true;
      }
    }
    let ev = rng.random(20) + 1;
    if (this.timeOfDay >= 16 && ev > 15) ev = 1;
    if (!this.hadTravelEvent) {
      if (ev <= 6) {
        await this.showMessage("", "ה ס פ י נ ה   נ ע צ ר ה", "");
        this.hadTravelEvent = true;
        await this.piratesFlow(this.guardCount, weightArg);
      } else if (ev === 7) {
        if (this.settings.navError) await this.changeTravelDestination();
      } else if (ev === 9 || ev === 10) {
        await this.showMessage("", "ה ס פ י נ ה   נ ע צ ר ה", "");
        this.s.putPic("deserted_win", 0, 142);
        let w = 0;
        let found = false;
        do {
          w += 1;
          const amount = Math.trunc(((rng.random(25) + 11) / 100) * ((this.money + this.cargoValue()) / BASE[w]));
          if (amount > 0) {
            this.s.writeAt(5, 16, vis(`${amount} טון ${WARE[w]}`));
            this.cargo[w] += amount;
            found = true;
          }
        } while (!found && w < WHEAT);
        this.hadTravelEvent = found;
        await this.io.waitKey();
        this.showHUD();
      } else {
        this.didArriveSafely = true;
      }
    }
  }

  // Estimated cost of one guard ship (no rng - must not advance game state).
  // Mirrors showGuardShipWindow's formula with the average denominator (250).
  estGuardPrice() {
    if (this.settings.fixedGuardPrice) return 75;
    let price = 5 * Math.trunc((this.cargoValue() + this.money) / 250);
    if (price < 75) price = 75;
    if (this.risk > 0) price += price / 3;
    return 5 * Math.trunc(price / 5);
  }

  async showGuardShipWindow() {
    const rng = this.rng;
    this.s.putPic("guards_win", 0, 142);
    let price;
    if (this.settings.fixedGuardPrice) price = 75;
    else {
      price = 5 * Math.trunc((this.cargoValue() + this.money) / (200 + rng.random(100)));
      if (price < 75) price = 75;
      if (this.risk > 0) price += price / 3;
      price = 5 * Math.trunc(price / 5);
    }
    if (price <= this.money) {
      let l1;
      let l2 = `${price} ש"ח`;
      if (this.risk === 0) l1 = "מחיר כל ספינת משמר";
      else {
        l1 = "בעלי ספינות המשמר דורשים";
        if (this.storm === this.country || this.storm === this.travelDest) l2 = `${l2}, בשל הים הסוער.`;
        else l2 = `${l2} בשל הסיכון המוגבר`;
      }
      this.showMessageNow(l1, l2, "כמה ספינות אתה רוצה ?");
      const def = Math.trunc(this.settings.guardReserve || 0);
      const quick = def > 0 && def * price <= this.money ? [{ label: "ברירת מחדל", value: def }] : [];
      for (;;) {
        this.s.writeAt(4, 17, "    ");
        this.guardCount = Math.trunc(await this.inputReal(4, 17, 3, 0, 999, quick));
        if (!(await this.cannotAfford(this.guardCount * price))) break;
      }
    } else {
      this.showMessageNow("אין לך כסף לשלם", "לספינות משמר", "");
      const k = await this.io.choiceKeys([
        { key: "enter", label: "המשך ללא משמר" },
        { key: "esc", label: "ביטול הפלגה" },
      ]);
      this.guardCount = k === "esc" ? -1 : 0;
    }
    if (this.guardCount > 0) this.money -= this.guardCount * price;
  }

  async doTravel(duration) {
    await this.showGuardShipWindow();
    if (this.guardCount < 0) {
      this.travelDest = this.travelStart;
      return;
    }
    let half = Math.trunc((this.travelEnd - this.timeOfDay) / 2);
    this.travelEnd -= half;
    let cur = this.travelStart;
    let destIdx = this.travelDest;
    let next = (WAYPOINTS[cur] && WAYPOINTS[cur][destIdx]) || 0;
    await this.animateTravel(COORD[cur], COORD[next || destIdx]);
    this.travelEnd = this.timeOfDay + half;
    if (next) cur = next;
    await this.doTravelEvent(cur, this.damage, this.totalWeight(), duration, this.risk);
    destIdx = this.travelDest;
    next = (WAYPOINTS[cur] && WAYPOINTS[cur][destIdx]) || 0;
    if (next > 0) {
      half = Math.trunc(half / 2);
      this.travelEnd -= half;
    }
    while (next > 0) {
      await this.animateTravel(COORD[cur], COORD[next]);
      this.travelEnd = this.timeOfDay + half;
      cur = next;
      next = (WAYPOINTS[cur] && WAYPOINTS[cur][destIdx]) || 0;
    }
    await this.animateTravel(COORD[cur], COORD[destIdx]);
    if (this.didArriveSafely) this.showMessageNow("", "ה ג ע ת   ב ש ל ו ם  ! !", "");
  }

  async travelFlow() {
    const s = this.s;
    this.travelStart = this.country;
    this.travelDest = this.country;
    this.risk = 0;
    this.guardCount = 0;
    let shouldAbort = false;
    this.putMapWindow();
    const full = {
      [TURKEY]: { x1: 144, y1: 2, x2: 182, y2: 9 },
      [ISRAEL]: { x1: 228, y1: 106, x2: 237, y2: 137 },
      [EGYPT]: { x1: 75, y1: 134, x2: 111, y2: 141 },
    };
    const rects = [];
    const travelLabels = [];
    for (let c = 1; c <= 3; c++) {
      if (c !== this.country) {
        rects.push(full[c]);
        travelLabels.push(COUNTRY_NAME[c]);
      }
    }
    const sel = await this.io.chooseOption(rects, { escapable: true, labels: travelLabels });
    let dest;
    if (this.country === TURKEY) dest = sel + 1;
    else if (this.country === ISRAEL) dest = sel * 2 - 1;
    else dest = sel;
    if (dest <= 0) return;
    this.travelDest = dest;
    const duration = this.travelStart === ISRAEL || this.travelDest === ISRAEL ? 4 : 8;
    if (this.timeOfDay + duration > 20) {
      await this.showMessage("השעה מאוחרת", "ולא תוכל להגיע", "לשם היום");
      this.travelDest = this.travelStart;
      shouldAbort = true;
    }
    if (!shouldAbort) {
      if (this.timeOfDay + duration > 16) {
        s.putPic("night_win", 0, 142);
        if (!(await this.io.yesNo({ col: 4, row: 17 }))) {
          this.guardCount = -1;
        } else {
          this.travelEnd = this.timeOfDay + duration;
          this.risk = this.storm === this.country || this.storm === this.travelDest ? 4 : 2;
          await this.doTravel(duration);
        }
      } else {
        if (this.storm === this.country || this.storm === this.travelDest) this.risk = 3;
        this.travelEnd = this.timeOfDay + duration;
        await this.doTravel(duration);
      }
    }
    if (this.guardCount < 0) this.travelDest = this.travelStart;
    this.country = this.travelDest;
    if (this.guardCount >= 0) {
      s.writeAt(33, 1, vis(COUNTRY[this.country]).padStart(6));
      this.toggleCountryHighlight(this.travelStart);
      this.toggleCountryHighlight(this.country);
      await this.io.waitKey();
    }
  }

  // ---------- day loop ----------
  async askOverweight() {
    if (this.totalWeight() > this.capacity) {
      this.s.putPic("overwght_win", 0, 142);
      return !(await this.io.yesNo({ col: 13, row: 16 }));
    }
    return false;
  }

  async playDay() {
    const s = this.s;
    const resuming = this.resumeMidday;
    this.resumeMidday = false;
    if (!resuming) {
      this.saveState();
      this.timeOfDay = 8;
      let shouldSkipDay = false;
      this.generateNewPrices();
      await this.showNewDayScreen();
      if (this.day === 7) {
        await this.showMessage("זהו יום המסחר האחרון.", "זכור למכור בסוף היום", "את כל סחורתך,");
        await this.showMessage("מכיון שהנקודות שתצבור", "תקבענה על פי סכום", "הכסף שיוותר בידך !");
      }
      // Port upgrade (toggleable): 10% overnight interest, whole shekels only
      if (this.settings.bankInterest && this.day > 1 && Math.trunc(this.bank / 10) > 0) {
        const interest = Math.trunc(this.bank / 10);
        await this.showMessage("הבנק שילם לך 10% ריבית", `תוספת: ${commas(interest)} ש"ח`, "על הפקדת הלילה");
        this.bank += interest;
      }
      this.updateStorm();
      await this.showStormScreen();
      if (this.day > 1) shouldSkipDay = await this.doDayStartEvent();
      if (this.day > 7 || shouldSkipDay) return;
    }

    s.putPic("mainscrn_scr", 0, 199);
    this.menuPic = s.getData(0, 0, 240, 142);
    this.toggleCountryHighlight(this.country);
    for (;;) {
      s.putData(this.menuPic, 0, 0);
      this.showHUD();
      this.saveState(true); // mid-day autosave: a crash/tab-kill resumes right here
      const rects = [
        { x1: 131, y1: 36, x2: 177, y2: 45 },
        { x1: 126, y1: 51, x2: 174, y2: 60 },
        { x1: 125, y1: 67, x2: 171, y2: 76 },
        { x1: 81, y1: 83, x2: 167, y2: 92 },
        { x1: 42, y1: 99, x2: 162, y2: 108 },
      ];
      const menuLabels = ["קנייה", "מכירה", "הפלגה", "בנק", "לנוח עד למחרת"];
      if (this.damage !== 0) {
        s.putPic("line_6_lin", 35, 123);
        rects.push({ x1: 33, y1: 114, x2: 160, y2: 123 });
        menuLabels.push("תיקון ספינה");
      }
      const chosen = await this.io.chooseOption(rects, {
        escapable: true,
        escLabel: "יציאה",
        labels: menuLabels,
      });
      if (chosen === 1) await this.showBuyWindow();
      else if (chosen === 2) await this.showSellWindow();
      else if (chosen === 3) {
        if (this.timeOfDay > 16) await this.showMessage("לא ניתן לנסוע", "בשעה כה מאוחרת", "");
        else if (this.damage > 1000) await this.showMessage("הנזק שנגרם לספינתך אינו", "מאפשר לך להפליג.", "");
        else if (!(await this.askOverweight())) await this.travelFlow();
      } else if (chosen === 4) {
        await this.showBankWindow();
      } else if (chosen === 5) {
        s.putPic("stay_win", 0, 142);
        if (this.day === 7) this.showMessageNow("שבוע המסחר הסתים,", "הקש אישור לסיום המשחק", "");
        const k = await this.io.choiceKeys([
          { key: "enter", label: this.day === 7 ? "סיום המשחק" : "סיום היום" },
          { key: "esc", label: "חזרה" },
        ]);
        if (k === "enter") break;
      } else if (chosen === 6) {
        if (this.closedRepairDay !== this.day) await this.showRepairWindow();
        else await this.showMessage("רציף התיקונים בנמל סגור", "היום - תאלץ להמתין", "עד מחר");
      } else if (chosen === -1) {
        this.showMessageNow("המשחק שמור מבוקר היום", "לצאת מהמשחק? (כ/ל)", "");
        const yes = await this.io.yesNo({ col: 15, row: 17 });
        if (yes) throw new GameExit();
        this.s.putData(this.preMsg, 10, 109);
      }
    }
    if (this.day < 7 && this.isUnableToRepair()) {
      this.day = 7;
      await this.showMessage("אין באפשרותך לתקן את", "הנזק שנגרם לספינתך-", "ה מ ש ח ק   ה ס ת י ם  ! !");
    }
  }

  async startWeek() {
    const s = this.s;
    s.putPic("intro_scr", 0, 199);
    this.mapData = null;
    this.bank = 0;
    this.capacity = 100;
    this.country = ISRAEL;
    this.storm = 0;
    this.damage = 0;
    this.closedRepairDay = this.settings.closedShipyard ? this.rng.random(7) + 1 : 0;
    this.cargo = { 1: 0, 2: 0, 3: 0 };
    this.money = 10000;
    this.lastDayStartEvent = 0;
    await this.io.waitKey();
  }

  // ---------- high scores ----------
  drawScoreRows(scores, page = 0) {
    const s = this.s;
    const start = page * 10;
    const slice = scores.slice(start, start + 10);
    slice.forEach((e, i) => {
      const row = 3 + 2 * (i + 1);
      s.writeAt(9, row, commas(Math.round(e.score)).padStart(10));
      s.writeAt(20, row, vis(e.name).padStart(16));
    });
    if (page === 1) {
      // overlay the baked rank numbers (1..10) with 11..20 in green on brown bg
      const brown = PALETTE[3];
      s.ctx.fillStyle = `rgb(${brown[0]},${brown[1]},${brown[2]})`;
      for (let i = 0; i < 10; i++) {
        const row = 3 + 2 * (i + 1);
        // rank labels sit at the right edge of each row (col 38, ~2 chars wide)
        s.ctx.fillRect(37 * 8, (row - 1) * 8, 16, 8);
        s.writeAt(38, row, String(start + i + 1).padStart(2), 1);
      }
    }
    // small page indicator at the bottom-left
    const pages = Math.ceil(scores.length / 10) || 1;
    if (pages > 1) {
      const brown = PALETTE[0];
      s.ctx.fillStyle = `rgb(${brown[0]},${brown[1]},${brown[2]})`;
      s.ctx.fillRect(0, 24 * 8, 80, 8);
      s.writeAt(2, 25, `${page + 1}/${pages}`, 1);
    }
  }

  async highScoreFlow(finalScore) {
    recordScore(finalScore);
    const s = this.s;
    // determine which leaderboard the current settings map to (best-effort —
    // backend computes authoritatively when we POST)
    const { scores } = await fetchScores("standard");
    // also fetch easy-mode list to know if we qualify there
    const { scores: easyScores } = await fetchScores("easy");
    const qualifiesStd = scores.length < 20 || scores.some((e) => e.score < finalScore);
    const qualifiesEasy = easyScores.length < 20 || easyScores.some((e) => e.score < finalScore);
    let finalList = scores;
    let finalMode = "standard";
    let finalEasyList = easyScores;
    if (qualifiesStd || qualifiesEasy) {
      s.putPic("in_10_scr", 0, 199);
      const drawName = (name) => {
        s.writeAt(13, 20, " ".repeat(16));
        const v = vis(name.slice(0, 16));
        if (v.length) s.writeAt(29 - v.length, 20, v);
      };
      const name = await this.io.inputName({ maxLen: 16, onChange: drawName });
      drawName(name);
      const posted = await postScore(name, finalScore, { ...this.settings });
      finalMode = posted.standard ? "standard" : "easy";
      if (finalMode === "standard") {
        finalList = posted.scores;
        // refresh easy list (unchanged but consistent)
        const { scores: e } = await fetchScores("easy");
        finalEasyList = e;
      } else {
        finalEasyList = posted.scores;
        const { scores: st } = await fetchScores("standard");
        finalList = st;
      }
    }
    await this.showScoresWithPaging({
      standard: finalList,
      easy: finalEasyList,
      initialMode: finalMode,
      missScore: !(qualifiesStd || qualifiesEasy) ? finalScore : null,
    });
  }

  async showScoresWithPaging({ standard, easy, initialMode = "standard", missScore = null }) {
    const s = this.s;
    let mode = initialMode;
    let page = 0;
    for (;;) {
      const list = mode === "standard" ? standard : easy;
      const pages = Math.max(1, Math.ceil(list.length / 10));
      if (page >= pages) page = 0;
      s.putPic("first_10_scr", 0, 199);
      this.drawScoreRows(list, page);
      // mode label overlay at top (replaces nothing existing on the title bar)
      const blue = PALETTE[0];
      s.ctx.fillStyle = `rgb(${blue[0]},${blue[1]},${blue[2]})`;
      s.ctx.fillRect(0, 0, 320, 8);
      const label = mode === "standard" ? "משחק מקורי" : "משחק עם הקלות";
      s.writeAt(1, 1, vis(label), mode === "standard" ? 1 : 2);
      if (missScore != null && mode === "standard") {
        s.putPic("score_sgn", 7, 130);
        const t = commas(missScore);
        s.writeAt(3, 10, t.padStart(missScore > 999 ? 6 : 4));
      }
      const keys = [];
      if (mode === "standard") {
        if (pages > 1 && page === 0) keys.push({ key: "next", label: "עמוד 2 ►" });
        if (pages > 1 && page === 1) keys.push({ key: "prev", label: "◄ עמוד 1" });
        keys.push({ key: "easy", label: "מצב הקלות ⇄" });
      } else {
        if (pages > 1 && page === 0) keys.push({ key: "next", label: "עמוד 2 ►" });
        if (pages > 1 && page === 1) keys.push({ key: "prev", label: "◄ עמוד 1" });
        keys.push({ key: "standard", label: "משחק מקורי ⇄" });
      }
      keys.push({ key: "back", label: "◄ חזרה" });
      const k = await this.io.choiceKeys(keys);
      if (k === "next") page = 1;
      else if (k === "prev") page = 0;
      else if (k === "easy") {
        mode = "easy";
        page = 0;
      } else if (k === "standard") {
        mode = "standard";
        page = 0;
      } else return;
    }
  }

  // ---------- main run ----------
  async run(continueSaved) {
    try {
      let restored = false;
      if (continueSaved && this.loadState()) {
        restored = true;
      } else {
        this.s.putPic("credits_scr", 0, 199);
        await this.io.waitKey();
        this.settings = loadSettings();
      }
      for (;;) {
        if (!restored) {
          this.day = 1;
          await this.startWeek();
        }
        restored = false;
        this.io.setHelpRenderer(() => this.renderHelp());
        while (this.day <= 7) {
          await this.playDay();
          this.day += 1;
        }
        this.io.setHelpRenderer(null);
        await this.highScoreFlow(this.money + this.bank);
        deleteSave();
        this.s.clearBlue();
        this.showMessageNow("האם אתה מעונין", "במשחק נוסף ? (כ/ל)", "");
        const again = await this.io.yesNo({ col: 7, row: 16 });
        if (!again) return;
      }
    } catch (e) {
      if (e && (e.gameAbort || e.gameExit)) {
        this.io.setHelpRenderer(null);
        return;
      }
      throw e;
    }
  }
}

// Standalone high-scores screen (from the title menu)
export async function showHighScoresView(screen, io) {
  try {
    const [{ scores: standard }, { scores: easy }] = await Promise.all([
      fetchScores("standard"),
      fetchScores("easy"),
    ]);
    let mode = "standard";
    let page = 0;
    for (;;) {
      const list = mode === "standard" ? standard : easy;
      const pages = Math.max(1, Math.ceil(list.length / 10));
      if (page >= pages) page = 0;
      screen.putPic("first_10_scr", 0, 199);
      const start = page * 10;
      const slice = list.slice(start, start + 10);
      slice.forEach((e, i) => {
        const row = 3 + 2 * (i + 1);
        screen.writeAt(9, row, commas(Math.round(e.score)).padStart(10));
        screen.writeAt(20, row, vis(e.name).padStart(16));
      });
      if (page === 1) {
        const brown = PALETTE[3];
        screen.ctx.fillStyle = `rgb(${brown[0]},${brown[1]},${brown[2]})`;
        for (let i = 0; i < 10; i++) {
          const row = 3 + 2 * (i + 1);
          screen.ctx.fillRect(37 * 8, (row - 1) * 8, 16, 8);
          screen.writeAt(38, row, String(start + i + 1).padStart(2), 1);
        }
      }
      // mode header (top of screen)
      const blue = PALETTE[0];
      screen.ctx.fillStyle = `rgb(${blue[0]},${blue[1]},${blue[2]})`;
      screen.ctx.fillRect(0, 0, 320, 8);
      const label = mode === "standard" ? "משחק מקורי" : "משחק עם הקלות";
      screen.writeAt(1, 1, vis(label), mode === "standard" ? 1 : 2);
      // page indicator at the bottom
      if (pages > 1) {
        screen.ctx.fillStyle = `rgb(${blue[0]},${blue[1]},${blue[2]})`;
        screen.ctx.fillRect(0, 24 * 8, 80, 8);
        screen.writeAt(2, 25, `${page + 1}/${pages}`, 1);
      }
      const keys = [];
      if (pages > 1 && page === 0) keys.push({ key: "next", label: "עמוד 2 ►" });
      if (pages > 1 && page === 1) keys.push({ key: "prev", label: "◄ עמוד 1" });
      if (mode === "standard") keys.push({ key: "easy", label: "מצב הקלות ⇄" });
      else keys.push({ key: "standard", label: "משחק מקורי ⇄" });
      keys.push({ key: "back", label: "◄ חזרה" });
      const k = await io.choiceKeys(keys);
      if (k === "next") page = 1;
      else if (k === "prev") page = 0;
      else if (k === "easy") {
        mode = "easy";
        page = 0;
      } else if (k === "standard") {
        mode = "standard";
        page = 0;
      } else return;
    }
  } catch (e) {
    if (!(e && e.gameAbort)) throw e;
  }
}
